import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort } from "../format.js";
import {
  Async, Badge, Button, Card, Chips, Empty, Grid, KPI, Money, Table, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";
import { SendModal } from "./InvoiceEditor.jsx";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "draft", label: "Draft" },
  { key: "overdue", label: "Overdue" },
  { key: "paid", label: "Paid" },
];

const BUCKETS = [
  { key: "current", label: "Current" },
  { key: "d1_30", label: "1–30 days" },
  { key: "d31_60", label: "31–60 days" },
  { key: "d60_plus", label: "60+ days" },
];

export default function Invoices() {
  const { settings, reloadApp, me } = useApp();
  const canManage = !!me.permissions["invoices.manage"];
  const toast = useToast();
  const sym = settings.currency_symbol;
  const [filter, setFilter] = useState("all");
  const [sending, setSending] = useState(null);
  const state = useAsync(() => api.get(`invoices?status=${filter}`), [filter]);

  const ageingOf = (ageing, key) => ageing.find((a) => a.bucket === key) || { amount: 0, count: 0 };

  return (
    <>
      <TopBar crumb="Get paid" title="Invoices">
        <Button
          onClick={() =>
            api.download("exports/invoices", "invoices.csv").catch((e) => toast.push(e.message, "err"))
          }
        >
          ⤓ Export CSV
        </Button>
        {canManage && (
          <Button kind="pri" onClick={() => navigate("/invoices/new")}>
            ＋ New invoice
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(d) => (
            <>
              <Grid cols={4}>
                {BUCKETS.map((b) => {
                  const cell = ageingOf(d.ageing, b.key);
                  return (
                    <KPI
                      key={b.key}
                      label={b.label}
                      value={money(cell.amount, sym)}
                      tone={b.key !== "current" && Number(cell.amount) > 0 ? "danger" : undefined}
                      sub={`${cell.count} invoice${Number(cell.count) === 1 ? "" : "s"}`}
                    />
                  );
                })}
              </Grid>

              <Card
                className="mt"
                bare
                title={<Chips options={FILTERS} value={filter} onChange={setFilter} />}
              >
                <Table
                  rows={d.list}
                  onRowClick={(r) => navigate(`/invoices/${r.id}`)}
                  empty={<Empty>No invoices match this filter.</Empty>}
                  columns={[
                    {
                      key: "number",
                      label: "Invoice",
                      render: (r) => (
                        <>
                          <b>{r.number}</b>
                          {r.po_number && <div className="tiny muted">PO {r.po_number}</div>}
                        </>
                      ),
                    },
                    {
                      key: "client_name",
                      label: "Client",
                      render: (r) => (
                        <>
                          {r.client_name}
                          {r.project_name && <div className="tiny muted">{r.project_name}</div>}
                        </>
                      ),
                    },
                    {
                      key: "issue_date",
                      label: "Issued",
                      render: (r) => <span className="sm dim">{fmtDateShort(r.issue_date)}</span>,
                    },
                    {
                      key: "due_date",
                      label: "Due",
                      render: (r) => {
                        const late = r.display_status === "overdue";
                        return (
                          <span
                            className="sm"
                            style={late ? { color: "var(--danger)", fontWeight: 600 } : { color: "var(--ink-2)" }}
                          >
                            {fmtDateShort(r.due_date)}
                            {late && <div className="tiny">{r.days_late}d late</div>}
                          </span>
                        );
                      },
                    },
                    { key: "total", label: "Total", align: "right", render: (r) => <Money v={r.total} sym={sym} /> },
                    {
                      key: "balance",
                      label: "Balance",
                      align: "right",
                      render: (r) =>
                        Number(r.balance) ? <Money v={r.balance} sym={sym} bold /> : <span className="muted">—</span>,
                    },
                    { key: "display_status", label: "Status", render: (r) => <Badge status={r.display_status} /> },
                    {
                      key: "act",
                      label: "",
                      align: "right",
                      render: (r) =>
                        r.status === "draft" && canManage ? (
                          <Button
                            sm
                            kind="gho"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSending(r.id);
                            }}
                          >
                            Send
                          </Button>
                        ) : (
                          <span className="muted">›</span>
                        ),
                    },
                  ]}
                />
              </Card>
            </>
          )}
        </Async>
      </Wrap>

      {sending && (
        <SendModal
          invoiceId={sending}
          onClose={() => setSending(null)}
          onSent={() => {
            state.reload();
            reloadApp();
            toast.push("Invoice sent");
          }}
        />
      )}
    </>
  );
}
